import { ReactNode } from 'react';
import { IControlProps, IPartialFormGroup } from '../types';
import FormControl from '../FormControl';
import DateField, { IDateFieldProps } from '../../atoms/DateField';
import { useHookFormController } from '../../hooks/useHookFormController';
import { dateToIsoDate, isoToDate } from '../../lib/dateFormValue';

export type IDateFieldControlProps = Omit<IDateFieldProps, 'value' | 'onChange' | 'defaultValue' | 'type'> &
    IControlProps & {
        formGroupProps?: IPartialFormGroup;
        label?: ReactNode;
        tooltip?: ReactNode;
        required?: boolean;
        onValueChange?: (value: Date | null) => void;
        isValueAsString?: boolean;
        /** Очищенное поле — `undefined` вместо `null`: ключ не попадает в JSON (так формы админки отдавали дату серверу) */
        clearToUndefined?: boolean;
    };

const DateFieldControl = ({
    name,
    rules,
    defaultValue,
    control,
    shouldUnregister,
    label,
    tooltip,
    required,
    formGroupProps,
    hideErrorMessage,
    onValueChange,
    isValueAsString,
    clearToUndefined,
    readOnly,
    'aria-label': ariaLabel,
    ...props
}: IDateFieldControlProps) => {
    const Controller = useHookFormController();

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            shouldUnregister={shouldUnregister}
            defaultValue={defaultValue}
            render={({ field: { onChange, value, ...fieldProps } }) => {
                // В isValueAsString-режиме значение приходит строкой (в т.ч. RU-формат
                // «ДД.ММ.ГГГГ» от ToString() на сервере). Нормализуем через safeDate,
                // чтобы пикер показал и не-ISO ввод; нераспознанное оставляем как есть.
                const currentIso = isValueAsString
                    ? (dateToIsoDate(value) ?? (typeof value === 'string' ? value : null))
                    : dateToIsoDate(value);

                return (
                    <FormControl
                        readonlyAppearance={readOnly || props.disabled ? 'field' : undefined}
                        name={name}
                        label={label}
                        tooltip={tooltip}
                        required={required}
                        control={control}
                        hideErrorMessage={hideErrorMessage}
                        {...formGroupProps}
                    >
                        <DateField
                            {...fieldProps}
                            {...props}
                            readOnly={readOnly}
                            // без своего имени поле названо подписью группы (FormGroup)
                            aria-label={ariaLabel}
                            value={currentIso}
                            onChange={(next: string | null) => {
                                if (readOnly) return;
                                const iso = typeof next === 'string' ? next : null;
                                if (iso === currentIso) return;
                                if (iso === null && clearToUndefined) {
                                    onChange(undefined);
                                } else if (isValueAsString) {
                                    onChange(iso);
                                } else {
                                    onChange(isoToDate(iso));
                                }
                                if (onValueChange) onValueChange(isoToDate(iso));
                            }}
                        />
                    </FormControl>
                );
            }}
        />
    );
};

export default DateFieldControl;
